import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getContext, retryContextVault } from "../services/contexts";
import type { BrowserContextSummary } from "../types/context";
import SessionList from "./SessionList";
import "./ContextsPage.css";

export default function ContextSessionsPage() {
  const { contextId } = useParams<{ contextId: string }>();
  if (contextId === undefined) {
    return <main className="contexts-shell"><div role="alert">Identifiant de contexte manquant.</div></main>;
  }
  // A route change must not render the previous context's readiness or session
  // actions, even during the render before the previous effect is cleaned up.
  return <ContextSessionsContent key={contextId} contextId={contextId} />;
}

function ContextSessionsContent({ contextId }: { contextId: string }) {
  const [context, setContext] = useState<BrowserContextSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState({ revision: 0, retryVault: false });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setLoading(true);
    setContext(null);
    const load = async (retryVault = false) => {
      try {
        const summary = await (retryVault ? retryContextVault(contextId) : getContext(contextId));
        if (cancelled) return;
        if (summary.id !== contextId) throw new Error("Le contexte reçu ne correspond pas à cette page.");
        setContext(summary);
        setError(null);
      } catch (cause: unknown) {
        if (cancelled) return;
        // Do not keep an old ready summary actionable after a failed refresh.
        setContext(null);
        setError(cause instanceof Error ? cause.message : "Impossible de charger le contexte.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Serialize reads: the next refresh starts only after this request
          // settles. A user retry POST is never repeated by this timer.
          timer = setTimeout(() => { void load(); }, 5000);
        }
      }
    };

    void load(request.retryVault);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [contextId, request]);

  if (loading) {
    return <main className="contexts-shell"><div className="contexts-banner" role="status">Chargement du contexte…</div></main>;
  }

  if (error !== null || context === null) {
    return (
      <main className="contexts-shell">
        <div className="contexts-page">
          <div className="contexts-banner contexts-banner-error" role="alert">{error ?? "Contexte introuvable."}</div>
          <button className="contexts-button" onClick={() => setRequest(current => ({ revision: current.revision + 1, retryVault: false }))}>Réessayer le chargement</button>
          <Link className="contexts-link-button" to="/contexts">Retour aux contextes</Link>
        </div>
      </main>
    );
  }

  return (
    <>
    {context.vault_status && (
      <div className="contexts-banner" role="status">
        {context.vault_status === "ready" ? "Coffre prêt" : context.vault_status === "pending" ? "Coffre en préparation" : "Coffre en erreur"}
        {context.vault_error && <p>{context.vault_error}</p>}
        {context.vault_status === "error" && context.vault_retryable && (
          <button className="contexts-button" onClick={() => setRequest(current => ({ revision: current.revision + 1, retryVault: true }))}>Réessayer le coffre</button>
        )}
      </div>
    )}
    <SessionList
      contextId={contextId}
      contextName={context.name}
      contextSummary={context}
    />
    </>
  );
}
